import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Switch,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { FC } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { joiResolver } from "@hookform/resolvers/joi";
import { useMutation } from "react-query";
import { toast } from "react-toastify";
import { ApiKeyPayload } from "../../../../../types";
import { AxiosError } from "../../../../../utils/api/axiosErrorType";
import { DatePickerComponent } from "../../../../../ui/atoms/DatePicker";
import { ApiKeysAPI } from "../../../../../utils/api/requests/api-keys-requests";
import { getAddApiKeyShema } from "../../../../../utils/schemas/ApiKeySchemas";
import { theme } from "../../../../../theme";
import { useTranslation } from "react-i18next";

interface AddApiKeyModalProps {
  open: boolean;
  handleClose: () => void;
  refetch: () => void;
}

export const AddApiKeyModal: FC<AddApiKeyModalProps> = ({
  open,
  handleClose,
  refetch,
}) => {
  const { t } = useTranslation();
  const form = useForm<ApiKeyPayload>({
    mode: "onChange",
    resolver: joiResolver(getAddApiKeyShema()),
  });
  const { handleSubmit, control } = form;
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { mutate, isLoading } = useMutation({
    mutationFn: ApiKeysAPI.addApiKey,
    onSuccess: () => {
      refetch();
      toast.success(t("apiKeys.key_added"));
      handleClose();
    },
    onError: (error) => {
      const err = error as AxiosError;
      toast.error(err?.response?.data?.detail);
    },
  });

  const onSubmit = async (formData: ApiKeyPayload) => {
    mutate(formData);
  };

  if (!open) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={isMobile}
    >
      <FormProvider {...form}>
        <Box component={"form"} onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle id="alert-dialog-title">
            {t("apiKeys.createModal.header")}
          </DialogTitle>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              width: { xs: "sm", sm: "350px", md: "350px" },
              minHeight: "350px",
            }}
          >
            <DatePickerComponent
              name={"expiration_at"}
              isEditing={true}
              disabled={false}
            />
            <Controller
              control={control}
              defaultValue={false}
              name="read_only"
              render={({
                field: { ref, onChange, value, ...field },
                fieldState: { error },
              }) => (
                <Box sx={{ mt: "12px", display: "flex", alignItems: "center" }}>
                  <Typography>{t("apiKeys.read_only")}</Typography>
                  <Switch onChange={onChange} value={value} />
                </Box>
              )}
            />
          </DialogContent>
          <DialogActions sx={{ marginBottom: "10px", px: "24px" }}>
            <Button variant="outlined" onClick={handleClose}>
              {t("cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={isLoading}
            >
              {t("create")}
            </Button>
          </DialogActions>
        </Box>
      </FormProvider>
    </Dialog>
  );
};
